"use client";

import { memo, useCallback, useState } from "react";
import {
	HmcAccordion,
	HmcCheckbox,
	HmcIcon,
} from "@adi-ctx/harmonic-core-components-react";
import { withHydrationFixed, type TFilterSubcategory } from "@/components";
import { t } from "@/utils";
import type { TPropsWithChildren } from "@/types";

type FilterSubcategoryProps = {
	readonly title: string;
	readonly options: TFilterSubcategory[];
	readonly selectedOptions: string[] | undefined;
	readonly onChange: (option: TFilterSubcategory, isChecked: boolean) => void;
};

const defaultVisibleOptionCount = 8;

/**
 * Displays a list of "options" with checkboxes in an accordion.
 * "onChange" is called when an option is checked/unchecked.
 * Based on the provided "selectedOptions" checkboxes are checked by default.
 * @constructor
 */
function FilterSubcategory(props: FilterSubcategoryProps) {
	const ContentWithHydrationFixed = withHydrationFixed(
		({ children }: TPropsWithChildren) => <div>{children}</div>
	);
	const [isShowAll, setIsShowAll] = useState(false);

	const onOptionChanged = useCallback(
		(option: TFilterSubcategory, isChecked: boolean) => {
			if (typeof props.onChange === "function") {
				props.onChange(option, isChecked);
			}
		},
		[props]
	);

	return (
		<HmcAccordion isOpen size="md" className="border-b">
			<div slot="header" data-testid="filter-subcategory-title">
				{props.title}{" "}
				{props.selectedOptions?.length
					? `(${props.selectedOptions?.length})`
					: ""}
			</div>

			<div slot="content" className="pb-4 pt-3">
				<ContentWithHydrationFixed>
					<div
						className="overflow-hidden"
						style={{
							maxHeight: isShowAll
								? "auto"
								: `${31 * defaultVisibleOptionCount}px`,
						}}
					>
						{props.options.map((option) => {
							const label = getLabel(option);

							return (
								<div
									key={option.label}
									className="w-full overflow-hidden whitespace-nowrap"
								>
									<HmcCheckbox
										title={option.label}
										label={`${label} (${option.nrResults})`}
										isChecked={props.selectedOptions?.includes(option.id)}
										size="xs"
										data-testid="filter-subcategory-checkbox"
										onChanged={(e) => {
											onOptionChanged(option, e.detail.checked);
										}}
									/>
								</div>
							);
						})}
					</div>

					{props.options.length > defaultVisibleOptionCount && (
						<span
							className="hmc-button-s -ml-[3px] mt-3 flex items-center gap-1 font-semibold text-[#0067B9] hover:cursor-pointer hover:underline"
							data-testid="filter-subcategory-toggle"
							onClick={() => {
								setIsShowAll(!isShowAll);
							}}
						>
							<HmcIcon
								name={`${isShowAll ? "subtract-m" : "add-m"}`}
								data-testid="filter-subcategory-toggle-icon"
							/>
							{t(isShowAll ? "filters.show-less" : "filters.show-all", {
								number: props.options.length,
							})}
						</span>
					)}
				</ContentWithHydrationFixed>
			</div>
		</HmcAccordion>
	);
}

export default memo(FilterSubcategory);

// Calculates how many characters from the label fits into the available horizontal space.
// If the whole label doesn't fit, truncate it (ex: "long labe... (1)")
function getLabel(
	option: TFilterSubcategory,
	maxAvailableHorizontalSpace = 192
): string {
	if (typeof window === "undefined") return option.label; // server-side rendering

	const div = window.document.createElement("div");
	const span = window.document.createElement("span");
	div.appendChild(span);
	span.style.whiteSpace = "nowrap";
	span.className = "hmc-body-xs";
	window.document.body.querySelector("main")?.appendChild(div);

	const text = `${option.label} (${option.nrResults})`.replace(/\s/g, "n");
	// number of characters from the option.label that would be visible (no overflow)
	let fit = 0;

	// eslint-disable-next-line @typescript-eslint/prefer-for-of
	for (let i = 0; i < text.length; ++i) {
		span.innerText += text[i];
		fit++;
		// keep adding chars from text into the span, until it gets overflown
		// then we take the 'fit' value, that will be the max number of characters from the label that fits
		if (span.getBoundingClientRect().width > maxAvailableHorizontalSpace) break;
	}

	window.document.body.querySelector("main")?.removeChild(div);

	const nrResults = String(` (${option.nrResults})`);
	let { label } = option;

	if (text.length > fit) {
		label = option.label.slice(0, fit - nrResults.length - 3) + "..."; // ex: "abc... (2)"
	}

	return label;
}
